/**
 * Wraps a module: enforces the scope/parent gate and the per-child requirement,
 * rendering a friendly fallback rather than a blank screen.
 */
import type { ReactNode } from 'react';
import { canAccess, canManageFamily } from '@solandia/core';
import { useI18n } from '../i18n';
import { useFamily } from '../family/FamilyContext';
import { EmptyState } from '../components/ui';
import type { ModuleDef } from './registry';

export function ModuleGuard({
  module,
  children,
}: {
  module: ModuleDef;
  children: ReactNode;
}) {
  const { t } = useI18n();
  const { membership, activeChild, children: kids } = useFamily();

  // A `null` scope (e.g. Home) is a family-wide surface: visible to any active
  // member, no scope gate. 'parent' scope requires family-admin; any other named
  // scope is checked via canAccess.
  const allowed =
    module.scope == null
      ? true
      : module.scope === 'parent'
        ? canManageFamily(membership)
        : canAccess(membership, module.scope);

  if (!allowed) {
    return <EmptyState emoji="🔒" title={t.errors.forbidden} />;
  }

  if (module.perChild) {
    if (kids.length === 0) {
      return (
        <EmptyState
          emoji="🐥"
          title={t.children.none}
          hint={t.children.noneLead}
        />
      );
    }
    if (!activeChild) {
      return <EmptyState emoji="👆" title={t.children.selectPrompt} />;
    }
  }

  return <>{children}</>;
}
